import { NavLink, Outlet } from 'react-router-dom';
import {
  DateRangeFilter,
  ErrorState,
  FilterBar,
  OriginTagsFilter,
  PageHeader
} from '../../../shared/components/index.js';
import { OLD_LEADS_CHILDREN } from '../../../shared/constants/nav.js';
import { useLeadsData } from '../hooks/useLeadsData.jsx';
import { buildMetaBarText } from '../utils/present-leads-html.js';
import { useEffect, useMemo } from 'react';

const LEADS_NAV = OLD_LEADS_CHILDREN;

function LeadsShellInner() {
  const {
    email,
    period,
    setPeriod,
    periodOptions,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    loading,
    error,
    data,
    metaBar: metaBarRaw,
    syncBusy,
    runSync,
    reload,
    cancelSearch,
    ensureLoaded,
    originTags,
    setOriginTags,
    originTagOptions,
    originTagsLoading,
    selectOriginMetaOnly,
    selectOriginAll,
    clearOriginTags
  } = useLeadsData();

  useEffect(() => {
    ensureLoaded();
  }, [ensureLoaded]);

  const metaBar = useMemo(() => {
    if (syncBusy && metaBarRaw) return metaBarRaw;
    if (!data) return metaBarRaw || '';
    return (
      buildMetaBarText(
        data.stageHistoryCoverage || (data.dataQuality && data.dataQuality.stageHistoryCoverage),
        data.kommoSync
      ) || metaBarRaw
    );
  }, [data, syncBusy, metaBarRaw]);

  return (
    <div
      className={
        'analytics-module analytics-module-leads' +
        (loading || syncBusy ? ' is-loading-period' : '')
      }
    >
      <PageHeader
        eyebrow="Old · Leads"
        title="Leads"
        subtitle={
          'Calidad, embudo, conversión y camino por campaña (gasto → reunión → reserva). El período arriba filtra todo el módulo.' +
          (email ? ` · ${email}` : '')
        }
        actions={
          <FilterBar label="Controles de Leads">
            <DateRangeFilter
              id="leads-period"
              value={period}
              options={periodOptions}
              disabled={syncBusy}
              onChange={setPeriod}
            />
            {period === 'custom' ? (
              <>
                <label className="analytics-field">
                  <span>Desde</span>
                  <input
                    type="date"
                    value={dateFrom}
                    disabled={syncBusy}
                    onChange={(e) => setDateFrom(e.target.value)}
                  />
                </label>
                <label className="analytics-field">
                  <span>Hasta</span>
                  <input
                    type="date"
                    value={dateTo}
                    disabled={syncBusy}
                    onChange={(e) => setDateTo(e.target.value)}
                  />
                </label>
              </>
            ) : null}
            <OriginTagsFilter
              options={originTagOptions}
              value={originTags}
              onChange={setOriginTags}
              disabled={syncBusy}
              loading={originTagsLoading}
              onSelectMetaOnly={selectOriginMetaOnly}
              onSelectAll={selectOriginAll}
              onClear={clearOriginTags}
            />
            <button
              type="button"
              className={'analytics-btn' + (loading ? ' is-loading' : '')}
              disabled={syncBusy}
              aria-busy={loading ? 'true' : 'false'}
              title="Consultar el período elegido"
              onClick={() => reload()}
            >
              {loading ? 'Buscando…' : 'Buscar'}
            </button>
            {loading ? (
              <button
                type="button"
                className="analytics-btn analytics-btn-ghost"
                title="Interrumpir la búsqueda en curso"
                onClick={() => cancelSearch()}
              >
                Cancelar
              </button>
            ) : null}
            <button
              type="button"
              className="analytics-btn analytics-btn-ghost"
              disabled={syncBusy}
              title="Sincroniza datos desde el CRM"
              onClick={runSync}
            >
              {syncBusy ? 'Sincronizando…' : 'Sincronizar datos'}
            </button>
          </FilterBar>
        }
      />

      {!data && !loading && !syncBusy && !error ? (
        <p className="analytics-empty">
          Elegí el período (y orígenes si aplica) y pulsá <strong>Buscar</strong>. No se consulta hasta entonces.
        </p>
      ) : null}

      {loading || syncBusy ? (
        <div className="analytics-leads-loading" role="status" aria-live="polite">
          <span className="analytics-leads-loading-dot" aria-hidden />
          {syncBusy
            ? 'Sincronizando Kommo… puede tardar unos minutos.'
            : 'Buscando período (Kommo + Meta)… podés cambiar el rango y volver a buscar.'}
        </div>
      ) : null}

      <nav className="leads-subnav" aria-label="Secciones de Leads">
        {LEADS_NAV.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            end={!!item.end}
            className={({ isActive }) => 'leads-subnav-link' + (isActive ? ' is-active' : '')}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      {metaBar ? (
        <p className="analytics-meta-bar" dangerouslySetInnerHTML={{ __html: metaBar }} />
      ) : null}

      <ErrorState message={error} onRetry={() => reload()} />

      <Outlet />
    </div>
  );
}

export default function LeadsLayout({ email }) {
  void email;
  return <LeadsShellInner />;
}
